import { faUser, faUserGroup, faUserPlus, faUserTag } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import ChooseModal, { Option as ChooseOption } from 'components/UI/ChooseModal/ChooseModal';
import clsx from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

import { populationFor } from '../../AddEmailCampaign';
import { getRecipientCount } from '../../getRecipientCount';
import { EmailCampaignRecipientType, EmailCampaignStepProps } from '../../types';

import ClientGroupsSubOptions from './ClientGroupsSubOptions';
import EditClientsDrawer from './EditClientsDrawer';
import TotalClientsBar from './TotalClientsBar';

import styles from './RecipientsStep.module.scss';

/**
 * Static config for the 4 mode cards. Each card represents one user intent
 * ("who do I want to email?"). The mapping to the backend `(population,
 * criteria)` pair lives in `AddEmailCampaign.tsx` — `populationFor` for
 * population, and the switch inside `persistStep(1)` for criteria.
 */
/**
 * Copy for the `TotalClientsBar` at the bottom of Step 1. Adapts to the
 * currently selected mode so the noun ("clients" / "leads" / "recipients")
 * matches the audience the user picked — otherwise a "leads" campaign
 * would still read "Total clients" and mislead the review.
 */
const ENTITY_LABELS: Record<EmailCampaignRecipientType, { title: string; description: string; count: string }> = {
    all_profiles: {
        title: 'Total recipients',
        description: 'View and edit the recipients your email will be sent to.',
        count: 'Recipients'
    },
    leads: {
        title: 'Total leads',
        description: 'View and edit the leads your email will be sent to.',
        count: 'Leads'
    },
    all_clients: {
        title: 'Total clients',
        description: 'View and edit the clients your email will be sent to.',
        count: 'Clients'
    },
    client_groups: {
        title: 'Total clients',
        description: 'View and edit the clients your email will be sent to.',
        count: 'Clients'
    }
};

const MODE_CARDS: Array<{
    id: EmailCampaignRecipientType;
    icon: FontAwesomeIconProps['icon'];
    title: string;
    description: string;
}> = [
    {
        id: 'all_profiles',
        icon: faUserGroup,
        title: 'All Profiles',
        description: 'Send to every client and lead in your list.'
    },
    {
        id: 'leads',
        icon: faUserPlus,
        title: 'Leads',
        description: 'Send to every lead in your list.'
    },
    {
        id: 'all_clients',
        icon: faUser,
        title: 'All Clients',
        description: 'Send to every client in your list.'
    },
    {
        id: 'client_groups',
        icon: faUserTag,
        title: 'Client Groups',
        description: 'Send to specific groups of clients by tag.'
    }
];

/**
 * Orchestrator for step 1 — Recipients.
 *
 * Renders one radio card per user intent (All Profiles / All Clients /
 * Client Groups / Leads). The population + criteria mapping to the backend
 * is fully derived from the picked mode — see `populationFor` and
 * `persistStep(1)` in `AddEmailCampaign.tsx`.
 *
 * Data flow:
 *   - The counters snapshot + multi-tag union fetch live in `AddEmailCampaign`
 *     (the wizard root) so they stay warm regardless of which step the user
 *     lands on first — important on draft resume, which can jump straight to
 *     step 3 without ever mounting this step. This component is a pure
 *     consumer of the resulting Redux slices.
 *   - Only `client_groups` reveals a tag picker; the other three modes are
 *     self-contained ("send to everyone in this pool"). The `Choose tag(s)`
 *     button below the mode cards opens the `ChooseModal` directly — the
 *     obsolete intermediate "Client Groups sub-options" radio group was
 *     removed since only `by_tag` was ever wired end-to-end.
 */
const RecipientsStep: React.FC<EmailCampaignStepProps> = ({ values, onChange }) => {
    const counters = useSelector((state: RootState) => state.emailCampaigns.counters);
    const fetchedCustomersTotal = useSelector((state: RootState) => state.emailCampaigns.customers.totalRecords);
    const quote = useSelector((state: RootState) => state.emailCampaigns.quote.data);
    const { clientTags, emailCampaignsBillingEnabled } = useSelector((state: RootState) => state.masterData.data);

    const [showTagDrawer, setShowTagDrawer] = React.useState(false);
    const [showEditDrawer, setShowEditDrawer] = React.useState(false);

    // Total shown in the bottom bar. Delegates to the shared `getRecipientCount`
    // helper so this display, the live quote fetch, and Step 1 Continue
    // validation all agree on what "N recipients" means for the current draft.
    // `null` = not computable yet (counters loading) → bar renders `-`.
    const totalAfterExclusions = React.useMemo<number | null>(
        () => getRecipientCount({ values, counters, fetchedCustomersTotal }),
        [values, counters, fetchedCustomersTotal]
    );

    // Build the tag drawer options from masterData + counters. Memoised so
    // the drawer doesn't reset its internal selected state on every render.
    const tagOptions: ChooseOption[] = React.useMemo(() => {
        const positiveTags = clientTags?.positive ?? [];
        return positiveTags.map(tag => {
            const count = counters?.byTag.find(entry => entry.tag === tag.id)?.records ?? 0;
            return { id: tag.id, label: tag.name, count };
        });
    }, [clientTags, counters]);

    const tagInitialValue: ChooseOption[] = React.useMemo(() => {
        return tagOptions.filter(opt => values.selectedTagIds.includes(String(opt.id)));
    }, [tagOptions, values.selectedTagIds]);

    // Any recipientType change resets the picker (`selectedClientIds`,
    // `excludedClientIds`) and the tag selection — those are references to
    // specific customer / tag ids picked inside a particular pool, so once
    // the pool shifts they no longer describe a coherent set. Same fix
    // rationale as CUD-5130's "0 Clients, 3 excluded" bug.
    const handleSelectRecipientType = (recipientType: EmailCampaignRecipientType) => {
        onChange({
            recipientType,
            selectedTagIds: [],
            selectedClientIds: 'all',
            excludedClientIds: []
        });
    };

    const handleTagSave = (selected: ChooseOption[]) => {
        onChange({
            selectedTagIds: selected.map(opt => String(opt.id))
        });
        setShowTagDrawer(false);
    };

    // Edit-clients drawer — opens only when the current mode has a
    // resolvable pool. `client_groups` needs at least one tag; the other
    // three modes always have a pool (whichever the counters reflect).
    // Keep this gate in sync with `isStepValidFor` for Step 1 in
    // `AddEmailCampaign.tsx`.
    const isEditEnabled = React.useMemo<boolean>(() => {
        if (values.recipientType === 'client_groups') return values.selectedTagIds.length > 0;
        return true;
    }, [values.recipientType, values.selectedTagIds.length]);

    // For non-`by_tag` modes we deliberately pass `undefined` so the picker
    // fetch skips the `criteria` URL param and the BE falls back to its
    // default (`with_email`, scoped by population). Sending `criteria=all`
    // triggers a server-side dispatch bug — see `EditClientsDrawer.criteria`
    // for the full rationale.
    const editDrawerCriteria: 'by_tag' | undefined = values.recipientType === 'client_groups' ? 'by_tag' : undefined;

    const editDrawerArgs: string | undefined =
        editDrawerCriteria === 'by_tag' && values.selectedTagIds.length > 0
            ? values.selectedTagIds.join(',')
            : undefined;

    const handleEditClick = () => {
        if (!isEditEnabled) return;
        setShowEditDrawer(true);
    };

    const handleEditSave = (selectedIds: 'all' | number[], excludedIds: number[]) => {
        onChange({
            selectedClientIds: selectedIds,
            // Exclude list is only meaningful when the drawer was in
            // exclude mode (Select All on). When the user toggled Select
            // All off and picked a positive list, we drop any excludes
            // collected before the toggle — the BE will materialise from
            // the positive list directly.
            excludedClientIds: selectedIds === 'all' ? excludedIds : []
        });
        setShowEditDrawer(false);
    };

    const handleEditClose = () => {
        setShowEditDrawer(false);
    };

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <h2 className={styles.title}>Recipients</h2>
                <p className={styles.description}>Choose who will receive your email.</p>

                <div className={styles.modeOptions}>
                    {MODE_CARDS.map(card => (
                        <button
                            key={card.id}
                            type="button"
                            className={clsx(styles.modeCard, {
                                [styles.selectedModeCard]: values.recipientType === card.id
                            })}
                            onClick={() => handleSelectRecipientType(card.id)}
                        >
                            <span className={styles.radio}>
                                {values.recipientType === card.id && <span className={styles.radioDot} />}
                            </span>
                            <FontAwesomeIcon
                                icon={card.icon}
                                className={styles.modeIcon}
                            />
                            <span className={styles.modeLabel}>
                                <span className={styles.modeTitle}>{card.title}</span>
                                <span className={styles.modeDescription}>{card.description}</span>
                            </span>
                        </button>
                    ))}
                </div>

                {values.recipientType === 'client_groups' && (
                    <ClientGroupsSubOptions
                        selectedTagIds={values.selectedTagIds}
                        onOpenTagDrawer={() => setShowTagDrawer(true)}
                    />
                )}

                <div className={styles.divider} />

                <TotalClientsBar
                    total={totalAfterExclusions}
                    excludedCount={values.excludedClientIds.length}
                    estimatedCost={emailCampaignsBillingEnabled ? quote?.total ?? null : null}
                    entityLabel={ENTITY_LABELS[values.recipientType]}
                    onEditClick={handleEditClick}
                    editDisabled={!isEditEnabled}
                />
            </div>

            <ChooseModal
                title="Add Tag(s)"
                subtitle="Please, select at least one tag."
                selectAllLabel="All Tags"
                show={showTagDrawer}
                value={tagInitialValue}
                options={tagOptions}
                onClose={() => setShowTagDrawer(false)}
                onSave={handleTagSave}
            />

            <EditClientsDrawer
                show={showEditDrawer}
                onClose={handleEditClose}
                onSave={handleEditSave}
                initialSelectedIds={values.selectedClientIds}
                initialExcludedIds={values.excludedClientIds}
                criteria={editDrawerCriteria}
                args={editDrawerArgs}
                population={populationFor(values.recipientType)}
            />
        </div>
    );
};

export default RecipientsStep;
